/**
 * JRS theme – GA4 menu tracking (dataLayer only; GTM forwards to GA4).
 *
 * Events: menu_open, menu_panel_open, menu_group_toggle, menu_link_click, menu_card_click.
 * Every push carries the full key set (null where not applicable), because GTM v2
 * dataLayer variables persist across pushes and a missing key would inherit a stale value.
 *
 * Listens at document level (capture phase) on hooks the menu markup already has,
 * so desktop-mega-nav / mobile-menu sections, snippets and JS stay untouched.
 * Never prevents or delays navigation.
 */
(function () {
  'use strict';

  if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

  var DESKTOP_ROOT = '[data-mega-nav]';
  var MOBILE_ROOT = '[data-mobile-menu]';
  var DESKTOP_DWELL_MS = 500;

  // Card containers: desktop mega-nav cards, mobile level-1 cards and level-2 tiles,
  // mobile featured banner and the stacked banner (mm-stack).
  var CARD_SELECTOR = '.mega-nav__card, .mobile-menu__card, .mobile-menu__banner, .mm-stack';

  // Badges and decorative spans are excluded from text reads.
  var TEXT_EXCLUDE = '.jrs-menu-badge, .mobile-menu__nav-badge, .mega-nav__card-badge, .mobile-menu__tile-badge, .mobile-menu__card-badge, [aria-hidden="true"]';

  /** Returns trimmed visible text of an element without badges, capped at 100 chars. */
  function cleanText(el) {
    if (!el) return null;
    var clone = el.cloneNode(true);
    var drop = clone.querySelectorAll(TEXT_EXCLUDE);
    for (var i = 0; i < drop.length; i++) drop[i].parentNode.removeChild(drop[i]);
    // Line breaks in headings ("Make your team<br>look professional") read as spaces
    var breaks = clone.querySelectorAll('br');
    for (var j = 0; j < breaks.length; j++) breaks[j].parentNode.replaceChild(document.createTextNode(' '), breaks[j]);
    var text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
    return text ? text.slice(0, 100) : null;
  }

  /** Returns pathname + search of a link (tel:/mailto: keep their scheme), or null. */
  function linkPath(a) {
    try {
      var url = new URL(a.getAttribute('href'), window.location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return url.protocol + url.pathname;
      return url.pathname + url.search;
    } catch (e) {
      return null;
    }
  }

  /** Returns the 1-based index of el among parent's children matching selector, or null. */
  function indexIn(parent, el, selector) {
    if (!parent || !el) return null;
    var items = parent.querySelectorAll(selector);
    for (var i = 0; i < items.length; i++) {
      if (items[i] === el) return i + 1;
    }
    return null;
  }

  /** Pushes one menu event with every key present. */
  function push(eventName, data) {
    try {
      var d = data || {};
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        menu_type: d.menu_type || null,
        menu_item: d.menu_item || null,
        menu_group: d.menu_group || null,
        link_text: d.link_text || null,
        link_url: d.link_url || null,
        link_level: d.link_level || null,
        card_type: d.card_type || null,
        position: d.position || null,
        state: d.state || null
      });
    } catch (e) {}
  }

  /* ── Desktop helpers ───────────────────────────────────────────── */

  /** Returns the level-1 title of the [data-mega-nav-item] holding el. */
  function desktopMenuItem(el) {
    var item = el.closest('[data-mega-nav-item]');
    return item ? cleanText(item.querySelector('.mega-nav__link-text')) : null;
  }

  /** Describes a non-card desktop link: level, group and position. */
  function describeDesktopLink(a) {
    var d = { menu_type: 'desktop', menu_item: desktopMenuItem(a) };
    var column = a.closest('.mega-nav__column');
    if (a.classList.contains('mega-nav__link')) {
      d.link_level = 1;
      d.position = indexIn(a.closest('.mega-nav__list'), a.closest('[data-mega-nav-item]'), '[data-mega-nav-item]');
    } else if (a.classList.contains('mega-nav__column-heading--link')) {
      d.link_level = 2;
      d.menu_group = cleanText(a);
      d.position = indexIn(a.closest('.mega-nav__columns'), column, '.mega-nav__column');
    } else if (a.classList.contains('mega-nav__column-link')) {
      d.link_level = 3;
      d.menu_group = column ? cleanText(column.querySelector('.mega-nav__column-heading')) : null;
      d.position = indexIn(a.closest('.mega-nav__column-links'), a, '.mega-nav__column-link');
    } else if (a.classList.contains('mega-nav__view-all')) {
      d.link_level = 2;
    }
    return d;
  }

  /* ── Mobile helpers ────────────────────────────────────────────── */

  /** Returns the level-1 title for el: the open level-2 panel's header title, else null. */
  function mobileMenuItem(el) {
    var panel = el.closest('[data-mobile-menu-level="2"]');
    return panel ? cleanText(panel.querySelector('.mobile-menu__header-title')) : null;
  }

  /** Describes a non-card mobile link: level, group and position. */
  function describeMobileLink(a) {
    var d = { menu_type: 'mobile', menu_item: mobileMenuItem(a) };
    var accordion = a.closest('[data-mobile-menu-accordion]');
    if (accordion) {
      d.link_level = 3;
      d.menu_group = cleanText(accordion.querySelector('.mobile-menu__accordion-title'));
      d.position = indexIn(a.closest('.mobile-menu__accordion-list'), a, '.mobile-menu__accordion-link');
    } else if (a.closest('[data-mobile-menu-level="2"]')) {
      // Level-2 direct links and "view all" links
      d.link_level = 2;
    } else {
      // Level-1 rows: nav links, utility and account links
      d.link_level = 1;
      d.menu_item = cleanText(a.querySelector('.mobile-menu__nav-text')) || null;
    }
    return d;
  }

  /** Describes a card link in either menu. */
  function describeCard(a, card, menuType) {
    var d = {
      menu_type: menuType,
      menu_item: menuType === 'desktop' ? desktopMenuItem(a) : mobileMenuItem(a)
    };
    var fullWidth = card.classList.contains('mega-nav__card--full-width') ||
      card.classList.contains('mobile-menu__card--full-width') ||
      card.classList.contains('mobile-menu__banner') ||
      card.classList.contains('mm-stack');
    d.card_type = fullWidth ? 'full' : 'small';
    d.link_text = cleanText(card.querySelector(
      '.mega-nav__card-heading, .mobile-menu__tile-heading, .mobile-menu__card-heading, .mobile-menu__card-pill-label, .mobile-menu__banner-heading, .mm-stack__heading'
    )) || cleanText(a);

    if (card.classList.contains('mega-nav__card')) {
      d.position = indexIn(card.closest('.mega-nav__cards'), card, '.mega-nav__card');
    } else if (card.classList.contains('mobile-menu__card')) {
      d.position = indexIn(card.closest('.mobile-menu__cards-grid'), card, '.mobile-menu__card');
    } else if (card.classList.contains('mm-stack')) {
      // Two CTAs share one heading; position tells them apart
      d.position = indexIn(card, a, '.mm-stack__btn');
    }
    return d;
  }

  /* ── menu_link_click / menu_card_click / menu_panel_open / menu_group_toggle ── */

  document.addEventListener('click', function (e) {
    try {
      var target = e.target;
      if (!target || !target.closest) return;

      // Mobile: level-1 row opens a level-2 panel
      var subTrigger = target.closest('[data-mobile-menu-submenu-trigger]');
      if (subTrigger) {
        var navItem = subTrigger.closest('.mobile-menu__nav-item');
        push('menu_panel_open', {
          menu_type: 'mobile',
          menu_item: cleanText(navItem ? navItem.querySelector('.mobile-menu__nav-text') : subTrigger)
        });
        return;
      }

      // Mobile: accordion toggle; state read after mobile-menu.js has run
      var accTrigger = target.closest('[data-mobile-menu-accordion-trigger]');
      if (accTrigger) {
        setTimeout(function () {
          push('menu_group_toggle', {
            menu_type: 'mobile',
            menu_item: mobileMenuItem(accTrigger),
            menu_group: cleanText(accTrigger.querySelector('.mobile-menu__accordion-title')),
            state: accTrigger.getAttribute('aria-expanded') === 'true' ? 'open' : 'close'
          });
        }, 0);
        return;
      }

      var a = target.closest('a[href]');
      if (!a) return;
      var menuType = a.closest(DESKTOP_ROOT) ? 'desktop' : (a.closest(MOBILE_ROOT) ? 'mobile' : null);
      if (!menuType) return;

      var card = a.closest(CARD_SELECTOR);
      var d;
      if (card) {
        d = describeCard(a, card, menuType);
        d.link_url = linkPath(a);
        push('menu_card_click', d);
        return;
      }

      // Desktop touch: the first tap on a level-1 link opens its dropdown instead of navigating
      // (desktop-mega-nav.js), so only a tap on an already-open item counts as a click
      if (menuType === 'desktop' && a.classList.contains('mega-nav__link') &&
          a.hasAttribute('aria-haspopup') && a.getAttribute('aria-expanded') !== 'true' &&
          ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
        return;
      }

      d = menuType === 'desktop' ? describeDesktopLink(a) : describeMobileLink(a);
      d.link_text = cleanText(a);
      d.link_url = linkPath(a);
      push('menu_link_click', d);
    } catch (err) {}
  }, true);

  /* ── menu_open (mobile) ────────────────────────────────────────── */

  document.addEventListener('mobileMenu:open', function () {
    push('menu_open', { menu_type: 'mobile' });
  });

  /* ── menu_panel_open (desktop) ─────────────────────────────────── */

  // Desktop dropdowns open on hover. Fires once per menu_item per page view,
  // and only when the dropdown stays open for DESKTOP_DWELL_MS.
  function initDesktop() {
    try {
      if (typeof MutationObserver === 'undefined') return;
      var links = document.querySelectorAll(DESKTOP_ROOT + ' [data-mega-nav-item] > .mega-nav__link[aria-expanded]');
      if (!links.length) return;
      var fired = {};
      var timers = [];

      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          try {
            var link = m.target;
            var i = Array.prototype.indexOf.call(links, link);
            if (i < 0) return;
            if (link.getAttribute('aria-expanded') === 'true') {
              if (timers[i]) return;
              var title = cleanText(link.querySelector('.mega-nav__link-text'));
              if (!title || fired[title]) return;
              timers[i] = setTimeout(function () {
                timers[i] = null;
                if (link.getAttribute('aria-expanded') !== 'true' || fired[title]) return;
                fired[title] = true;
                push('menu_panel_open', { menu_type: 'desktop', menu_item: title });
              }, DESKTOP_DWELL_MS);
            } else if (timers[i]) {
              clearTimeout(timers[i]);
              timers[i] = null;
            }
          } catch (err) {}
        });
      });

      for (var j = 0; j < links.length; j++) {
        observer.observe(links[j], { attributes: true, attributeFilter: ['aria-expanded'] });
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDesktop);
  } else {
    initDesktop();
  }
})();
